import type { EdgeConfigurationDraft } from "@sitescene/shared";

import { edgeSourceTypes } from "@sitescene/shared";
import {
	type MouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/buttons/button";

import {
	configurationApi,
	type ConfigurationPublication,
	type ConfigurationSnapshot,
} from "./configuration-api";
import { timestamp } from "./configuration-status";
import { IntegrationEditor, SourceEditor } from "./ConfigurationForms";
import { ConfigurationStatus } from "./ConfigurationStatus";
import {
	importSourcesCsv,
	newIntegration,
	newSource,
	sourceDescriptions,
	sourceLabels,
} from "./editor";
import { FormValidation } from "./form-validation";
import { configurationNavigationEvent } from "./navigation";
import { RevisionContents } from "./RevisionContents";
import {
	type ActiveForm,
	draftErrors,
	type FieldErrors,
	formDirty,
	openForm,
	stageForm,
} from "./workspace";

type Pending = {
	configuration?: EdgeConfigurationDraft;
	confirm?: boolean;
	id: string;
	kind: "publish" | "restore" | "save" | "undo";
	message: string;
	removal?: { id: string; kind: ActiveForm["kind"]; name: string };
	removedBy?: string;
	revision?: string;
	selection?: { id: string; kind: ActiveForm["kind"] };
	version: number;
};
type Tab = "Publication history" | "Review and publish" | "Sources";
type Undo = {
	id: string;
	kind: ActiveForm["kind"];
	name: string;
	operationId: string;
};
const tabs: Tab[] = ["Sources", "Review and publish", "Publication history"];

/** Installation-keyed workspace. Plaintext authoring values stay out of query/local storage caches. */
export function ConfigurationEditor(props: {
	installation: string;
	installationExternalId: string;
	org: string;
}) {
	return <Workspace {...props} key={`${props.org}/${props.installation}`} />;
}
function Workspace({
	installation,
	installationExternalId,
	org,
}: {
	installation: string;
	installationExternalId: string;
	org: string;
}) {
	const api = useMemo(
		() => configurationApi(installation, org),
		[installation, org],
	);
	const currentSnapshot = useRef<ConfigurationSnapshot | undefined>(undefined);
	const currentUndo = useRef<undefined | Undo>(undefined);
	const [snapshot, setSnapshot] = useState<ConfigurationSnapshot>();
	const [formGeneration, setFormGeneration] = useState(0);
	const [active, setActive] = useState<ActiveForm>();
	const [tab, setTab] = useState<Tab>("Sources");
	const [chooser, setChooser] = useState(false);
	const [type, setType] =
		useState<(typeof edgeSourceTypes)[number]>("rtsp_camera");
	const [importing, setImporting] = useState(false);
	const [csv, setCsv] = useState<{
		draft: EdgeConfigurationDraft;
		version: number;
	}>();
	const [history, setHistory] = useState<ConfigurationPublication[]>([]);
	const [inspected, setInspected] = useState<{
		draft: EdgeConfigurationDraft;
		revision: string;
		sourceUrls: Record<string, string>;
		unreadableSourceUrls: string[];
	}>();
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [errors, setErrors] = useState<Record<string, FieldErrors>>({});
	const [touched, setTouched] = useState<string[]>([]);
	const editedFields = useRef(new Set<string>());
	const [submitted, setSubmitted] = useState(false);
	const [busy, setBusy] = useState(false);
	const [pending, setPending] = useState<Pending>();
	const [undo, setUndo] = useState<Undo>();
	const [confirmation, setConfirmation] = useState(false);
	const [publicationConfirmed, setPublicationConfirmed] = useState(false);
	const [now, setNow] = useState(Date.now);
	const [refreshFailed, setRefreshFailed] = useState(false);
	const [reportedDesired, setReportedDesired] = useState<null | string>(null);
	const [reviewStale, setReviewStale] = useState(false);
	const [navigation, setNavigation] = useState<{
		cancel?: () => void;
		opener: HTMLElement | null;
		proceed: () => void;
	}>();
	const alive = useRef(false);
	const working = useRef(false);
	const fileGeneration = useRef(0);
	const formNode = useRef<HTMLDivElement>(null);
	const dialog = useRef<HTMLDialogElement>(null);
	const dirty = formDirty(active);
	const locked = busy || !!pending;
	const clearValidation = useCallback(() => {
		setErrors({});
		setTouched([]);
		editedFields.current.clear();
		setSubmitted(false);
	}, []);
	const select = (value: ActiveForm | undefined) => {
		fileGeneration.current++;
		setFormGeneration((value) => value + 1);
		setActive(value);
		setChooser(false);
		setImporting(false);
		setCsv(undefined);
		clearValidation();
	};
	const focusName = () =>
		window.setTimeout(
			() =>
				formNode.current
					?.querySelector<HTMLInputElement>('[data-field="name"] input')
					?.focus(),
			0,
		);
	const accept = useCallback(
		(value: ConfigurationSnapshot, selection?: Pending["selection"]) => {
			fileGeneration.current++;
			setFormGeneration((value) => value + 1);
			currentSnapshot.current = value;
			setReportedDesired(value.desiredRevision);
			setReviewStale(false);
			currentUndo.current = undefined;
			setSnapshot(value);
			setConfirmation(false);
			setPublicationConfirmed(false);
			setUndo(undefined);
			clearValidation();
			setActive(
				selection
					? openForm(value.draft, selection.kind, selection.id)
					: undefined,
			);
		},
		[clearValidation],
	);
	useEffect(() => {
		alive.current = true;
		let disposed = false;
		let polling = false;
		void api
			.read()
			.then((value) => {
				if (!disposed) accept(value);
			})
			.catch(() => {
				if (!disposed)
					setError(
						"The saved configuration could not be loaded. Retry to load the complete draft.",
					);
			});
		void api
			.history()
			.then((value) => {
				if (!disposed) setHistory(value);
			})
			.catch(() => {});
		const timer = window.setInterval(async () => {
			setNow(Date.now());
			if (working.current || polling) return;
			polling = true;
			const startedVersion = currentSnapshot.current?.version;
			try {
				const value = await api.status();
				if (
					working.current ||
					currentSnapshot.current?.version !== startedVersion
				)
					return;
				if (!disposed) {
					const previous = currentSnapshot.current;
					setReportedDesired(value.desiredRevision);
					if (
						previous &&
						(previous.desiredRevision !== value.desiredRevision ||
							previous.version !== value.version)
					) {
						setConfirmation(false);
						setReviewStale(true);
						setError(
							"The published configuration changed. Load the latest saved draft before publication.",
						);
					}
					setSnapshot((old) =>
						old ? { ...old, reports: value.reports } : old,
					);
					setRefreshFailed(false);
				}
			} catch {
				if (!disposed) setRefreshFailed(true);
			} finally {
				polling = false;
			}
		}, 15000);
		return () => {
			disposed = true;
			alive.current = false;
			window.clearInterval(timer);
		};
	}, [api, accept]);
	const guard = useCallback(
		(proceed: () => void, cancel?: () => void) => {
			if (locked || working.current) {
				setError("Resolve the pending operation before leaving this form.");
				cancel?.();
				return;
			}
			if (dirty)
				setNavigation({
					cancel,
					opener:
						document.activeElement instanceof HTMLElement
							? document.activeElement
							: null,
					proceed,
				});
			else proceed();
		},
		[dirty, locked],
	);
	useEffect(() => {
		const unload = (event: BeforeUnloadEvent) => {
			if (dirty || locked) {
				event.preventDefault();
				event.returnValue = "";
			}
		};
		const navigate = (event: Event) => {
			if (!(event instanceof CustomEvent) || (!dirty && !locked)) return;
			event.preventDefault();
			if (event.detail && typeof event.detail.proceed === "function")
				guard(event.detail.proceed, event.detail.cancel);
		};
		window.addEventListener("beforeunload", unload);
		window.addEventListener(configurationNavigationEvent, navigate);
		return () => {
			window.removeEventListener("beforeunload", unload);
			window.removeEventListener(configurationNavigationEvent, navigate);
		};
	}, [dirty, locked, guard]);
	useEffect(() => {
		if (navigation) dialog.current?.showModal();
		else dialog.current?.close();
	}, [navigation]);
	const cancelNavigation = () => {
		navigation?.cancel?.();
		navigation?.opener?.focus();
		setNavigation(undefined);
	};
	const navigateTo = (next: Tab) => {
		if (next === tab) return;
		guard(() => {
			setTab(next);
			window.setTimeout(
				() => document.getElementById(`tab-${next}`)?.focus(),
				0,
			);
			setChooser(false);
			setImporting(false);
			setInspected(undefined);
			setError("");
		});
	};
	const revealErrors = () =>
		window.setTimeout(() => {
			const input = formNode.current?.querySelector<HTMLElement>(
				'[aria-invalid="true"]',
			);
			let parent = input?.parentElement;
			while (parent) {
				if (parent instanceof HTMLDetailsElement) parent.open = true;
				parent = parent.parentElement;
			}
			input?.focus();
		}, 0);
	const knownFailure = (cause: unknown) =>
		cause instanceof ApiError &&
		[400, 401, 403, 404, 409, 422].includes(cause.statusCode);
	const failureMessage = (cause: unknown) =>
		cause instanceof ApiError && cause.statusCode === 409
			? "The saved draft changed or this operation no longer matches. Your form is intact. Load the latest saved draft before trying again."
			: cause instanceof ApiError &&
				  (cause.statusCode === 401 || cause.statusCode === 403)
				? "You no longer have permission to change this configuration. Your form is still shown."
				: "The draft could not be changed. Check the form and its dependencies, then retry. Your values are still shown.";
	const attachServerErrors = (cause: unknown) => {
		if (!(cause instanceof ApiError) || !active) return;
		const message = typeof cause.data === "string" ? cause.data : cause.message;
		// Match known diagnostics, never render arbitrary server payloads or credential values.
		let fields: FieldErrors = {};
		if (
			/Invalid CA certificate|CA bundle must contain PEM certificates only/.test(
				message,
			)
		)
			fields = {
				ca_bundle_pem:
					"Enter valid PEM certificates only, or clear this optional field.",
			};
		if (/Replace credentials when changing their host/.test(message)) {
			for (const [field, edit] of Object.entries(active.item.secrets))
				if (edit?.operation === "keep")
					fields[field] =
						"Enter replacement credentials for the changed connection.";
		}
		if (Object.keys(fields).length) {
			setErrors({ [active.item.id]: fields });
			setSubmitted(true);
			revealErrors();
		}
	};

	const invoke = (request: Pending) => {
		if (request.kind === "publish")
			return api.publish(request.version, request.id, !!request.confirm);
		if (request.kind === "restore" && request.revision)
			return api.restore(request.version, request.id, request.revision);
		if (request.kind === "undo" && request.removedBy)
			return api.undo(request.version, request.id, request.removedBy);
		if (request.kind === "save" && request.configuration)
			return api.save(request.version, request.id, request.configuration);
		throw new Error("Incomplete configuration operation");
	};
	const finish = async (request: Pending, result?: ConfigurationSnapshot) => {
		if (!alive.current) return false;
		// Confirmation comes from the mutation/receipt, not the success of subsequent polling.
		if (request.kind === "publish") {
			// Publication advances the draft version by one. Record that in the ref
			// the status poll compares against, so a failed refresh below cannot
			// make this operator's own publication look like someone else's.
			const published = {
				desiredRevision: request.id,
				version: request.version + 1,
			};
			if (currentSnapshot.current)
				currentSnapshot.current = { ...currentSnapshot.current, ...published };
			setPublicationConfirmed(true);
			setReportedDesired(request.id);
			setUndo(undefined);
			setConfirmation(false);
			setSnapshot((previous) =>
				previous ? { ...previous, ...published } : previous,
			);
			setNotice(request.message);
		}
		let value = result;
		if (!value) {
			try {
				value = await api.read();
			} catch {
				if (alive.current)
					setError(
						request.kind === "publish"
							? "Publication is confirmed. The latest draft and reports could not be refreshed; check again before another change."
							: "The operation completed, but the latest draft could not be loaded. Check again before another change.",
					);
				return false;
			}
		}
		if (!alive.current) return false;
		accept(value, request.selection);
		if (request.kind === "publish") setPublicationConfirmed(true);
		if (request.removal && value.version === request.version + 1) {
			const restored = { ...request.removal, operationId: request.id };
			currentUndo.current = restored;
			setUndo(restored);
		}
		if (request.removal || request.kind === "undo") {
			if (request.selection) focusName();
			else
				window.setTimeout(
					() => document.getElementById("configuration-add-source")?.focus(),
					0,
				);
		}
		setPending(undefined);
		setError("");
		setNotice(
			value.version === request.version + 1
				? request.message
				: "The operation completed. A newer saved draft is now displayed; review it before your next change.",
		);
		setCsv(undefined);
		setImporting(false);
		setChooser(false);
		if (request.kind === "publish")
			void api
				.history()
				.then((items) => {
					if (alive.current) setHistory(items);
				})
				.catch(() => {
					if (alive.current)
						setError(
							"Publication is confirmed. History could not be refreshed.",
						);
				});
		return true;
	};
	const execute = async (request: Pending, checkOnly = false) => {
		if (working.current) return false;
		working.current = true;
		setBusy(true);
		setPending(request);
		setError("");
		setNotice("");
		try {
			if (checkOnly) {
				const receipt = await api.operation(request.id);
				if (receipt) return await finish(request);
				setError(
					"The outcome is not confirmed. Check again or retry the same operation. Your draft and form are preserved.",
				);
				return false;
			}
			try {
				const result = await invoke(request);
				return await finish(request, "draft" in result ? result : undefined);
			} catch (cause) {
				if (!alive.current) return false;
				// A lost response is not a failed write. Look up this exact ID, never infer from bounded history.
				try {
					if (await api.operation(request.id)) return await finish(request);
				} catch {
					/* Remain unresolved. */
				}
				if (knownFailure(cause)) {
					setPending(undefined);
					setError(failureMessage(cause));
					attachServerErrors(cause);
					return false;
				}

				if (alive.current)
					setError(
						"The outcome is not confirmed. Check again or retry the same operation. Your draft and form are preserved.",
					);
				return false;
			}
		} catch {
			if (alive.current)
				setError(
					"The outcome could not be checked. Retry checking this same operation.",
				);
			return false;
		} finally {
			working.current = false;
			if (alive.current) setBusy(false);
		}
	};
	const save = async () => {
		if (!snapshot || !active || !dirty || locked) return false;
		const candidate = stageForm(snapshot.draft, active);
		const found = draftErrors(candidate, snapshot.unreadableSourceUrls);
		setErrors(found);
		setSubmitted(true);
		if (Object.keys(found).length) {
			revealErrors();
			return false;
		}
		return execute({
			configuration: structuredClone(candidate),
			id: crypto.randomUUID(),
			kind: "save",
			message: `${active.kind === "sources" ? "Source" : "Integration"} saved to draft. Review and publish to apply changes.`,
			selection: { id: active.item.id, kind: active.kind },
			version: snapshot.version,
		});
	};
	const abandon = () => {
		select(
			active?.baseline && snapshot
				? openForm(snapshot.draft, active.kind, active.item.id)
				: undefined,
		);
		setError("");
		setNotice("Changes abandoned. The saved draft is unchanged.");
	};
	const remove = (kind: ActiveForm["kind"], id: string) =>
		guard(() => {
			const snapshot = currentSnapshot.current;
			if (!snapshot) return;
			const item = snapshot.draft[kind].find((item) => item.id === id);
			if (!item) {
				select(undefined);
				return;
			}
			if (kind === "integrations") {
				const dependents = snapshot.draft.sources.filter(
					(source) => source.fields.integration_id === id,
				);
				if (dependents.length) {
					setError(
						`This integration is used by ${dependents.map((source) => source.fields.name).join(", ")}. Change or remove those staged sources first.`,
					);
					return;
				}
			}
			const list = snapshot.draft[kind];
			const index = list.findIndex((item) => item.id === id);
			const next = list[index + 1] ?? list[index - 1];
			const candidate = {
				...snapshot.draft,
				[kind]: list.filter((item) => item.id !== id),
			};
			void execute({
				configuration: candidate,
				id: crypto.randomUUID(),
				kind: "save",
				message: `Removed ${item.fields.name} from the draft. Publish to apply this removal.`,
				removal: { id, kind, name: item.fields.name },
				selection: next ? { id: next.id, kind } : undefined,
				version: snapshot.version,
			});
		});
	const undoRemoval = () =>
		guard(() => {
			const snapshot = currentSnapshot.current;
			const undo = currentUndo.current;
			if (!undo) {
				setNotice(
					"Undo expired because another change was saved to the draft.",
				);
				return;
			}
			if (snapshot && undo)
				void execute({
					id: crypto.randomUUID(),
					kind: "undo",
					message: `Restored ${undo.name} to the draft.`,
					removedBy: undo.operationId,
					selection: { id: undo.id, kind: undo.kind },
					version: snapshot.version,
				});
		});
	const reload = async () => {
		if (working.current || pending) return;
		if (
			snapshot &&
			!window.confirm(
				"Replace the current form and draft with the latest saved draft? Unsaved changes will be abandoned only if loading succeeds.",
			)
		)
			return;
		working.current = true;
		setBusy(true);
		try {
			const value = await api.read();
			if (alive.current) {
				accept(value);
				setError("");
				setNotice("Loaded the latest saved draft.");
				setCsv(undefined);
				setChooser(false);
				setImporting(false);
			}
		} catch {
			if (alive.current)
				setError(
					"The latest saved draft could not be loaded. Your current form and draft are unchanged.",
				);
		} finally {
			working.current = false;
			if (alive.current) setBusy(false);
		}
	};
	const beginAdd = () =>
		guard(() => {
			if (active && currentSnapshot.current)
				setActive(
					openForm(currentSnapshot.current.draft, active.kind, active.item.id),
				);
			setChooser(true);
			setImporting(false);
			setType("rtsp_camera");
			clearValidation();
		});
	const createSource = () => {
		select({ item: newSource(type), kind: "sources" });
		focusName();
	};
	const inspect = async (revision: string) => {
		if (working.current) return;
		working.current = true;
		setBusy(true);
		setError("");
		try {
			const value = await api.revision(revision);
			if (alive.current) setInspected(value);
		} catch {
			if (alive.current)
				setError("This revision could not be loaded. The draft is unchanged.");
		} finally {
			working.current = false;
			if (alive.current) setBusy(false);
		}
	};
	const copyRevision = (revision: string) => {
		if (
			!snapshot ||
			locked ||
			!window.confirm(
				"Copy this revision to the saved draft? This replaces the draft. It does not change the Edge Device until publication.",
			)
		)
			return;
		void execute({
			id: crypto.randomUUID(),
			kind: "restore",
			message:
				"Revision copied to the draft. Review and publish to apply changes.",
			revision,
			version: snapshot.version,
		});
	};
	const removals = snapshot?.removals;
	const removedSources = removals?.sources ?? [];
	const removedIntegrations = removals?.integrations ?? [];
	const requiresConfirmation =
		!!snapshot &&
		(!snapshot.draft.sources.length ||
			!!removedSources.length ||
			!!removedIntegrations.length);
	const confirmationLabel =
		removedSources.length && removedIntegrations.length
			? "Confirm source and integration removal"
			: removedIntegrations.length
				? "Confirm integration removal"
				: !snapshot?.draft.sources.length
					? "Confirm empty configuration publication"
					: "Confirm source removal";
	const publish = () => {
		if (!snapshot || dirty || locked) return;
		if (snapshot.unreadableSourceUrls.length) {
			setError(
				"Replace or remove the sources whose saved stream URL can't be read before publishing.",
			);
			return;
		}
		if (requiresConfirmation && !confirmation) {
			setError("Confirm the removal or empty configuration before publishing.");
			document.getElementById("configuration-confirmation")?.focus();
			return;
		}
		void execute({
			confirm: confirmation,
			id: crypto.randomUUID(),
			kind: "publish",
			message:
				"Configuration published. The Edge Device will apply it on contact, or automatically during installation.",
			version: snapshot.version,
		});
	};
	const updateActive = (next: ActiveForm) => {
		// Reverting a readable URL restores its version-bound keep operation.
		if (
			next.kind === "sources" &&
			next.baseline?.secrets.rtsp_url?.operation === "keep" &&
			next.item.secrets.rtsp_url?.operation === "replace" &&
			next.item.secrets.rtsp_url.value === snapshot?.sourceUrls[next.item.id]
		)
			next = {
				...next,
				item: {
					...next.item,
					secrets: {
						...next.item.secrets,
						rtsp_url: next.baseline.secrets.rtsp_url,
					},
				},
			};
		setActive(next);
		setError("");
		setNotice("");
		if (snapshot)
			setErrors(
				draftErrors(
					stageForm(snapshot.draft, next),
					snapshot.unreadableSourceUrls,
				),
			);
	};
	const activeErrors = active ? (errors[active.item.id] ?? {}) : {};
	const shownErrors = Object.fromEntries(
		Object.entries(activeErrors).filter(
			([key]) => submitted || touched.includes(key),
		),
	);
	const blur = (target: EventTarget) => {
		if (!(target instanceof HTMLElement) || !active || !snapshot) return;
		const field = target.closest<HTMLElement>("[data-field]")?.dataset.field;
		if (!field || !editedFields.current.has(field)) return;
		setTouched((previous) =>
			previous.includes(field) ? previous : [...previous, field],
		);
		setErrors(
			draftErrors(
				stageForm(snapshot.draft, active),
				snapshot.unreadableSourceUrls,
			),
		);
	};
	const readCsv = async (file?: File) => {
		if (!file || !snapshot) return;
		const version = snapshot.version;
		const generation = ++fileGeneration.current;
		try {
			if (file.size > 1024 * 1024) throw new Error("CSV exceeds 1 MiB.");
			const candidate = importSourcesCsv(await file.text(), snapshot.draft);
			if (generation !== fileGeneration.current) return;
			if (alive.current) {
				setCsv({ draft: candidate, version });
				setError("");
			}
		} catch {
			if (alive.current && generation === fileGeneration.current) {
				setCsv(undefined);
				setError(
					"CSV could not be imported. Check supported columns, required fields, unique Sensor IDs, and the 200-source limit.",
				);
			}
		}
	};
	const showFormErrors = (event: MouseEvent<HTMLButtonElement>) => {
		const id = event.currentTarget.dataset.formId;
		if (!snapshot || !id) return;
		if (id === active?.item.id) revealErrors();
		else
			guard(() => {
				const kind = snapshot.draft.sources.some((item) => item.id === id)
					? "sources"
					: "integrations";
				select(openForm(snapshot.draft, kind, id));
				setSubmitted(true);
				setErrors(errors);
				revealErrors();
			});
	};
	return (
		<section
			aria-label="Cloud configuration"
			className="min-w-0 space-y-5 break-words"
		>
			<header>
				<h2 className="text-xl font-semibold">Cloud configuration</h2>
				<p className="text-sm text-secondary">
					Configure Lookout from the saved cloud draft. The Edge Device changes
					only after publication.
				</p>
			</header>
			{!snapshot ? (
				<>
					<p>{error || "Loading the saved configuration…"}</p>
					{error && (
						<Button isDisabled={busy} onClick={() => void reload()} size="sm">
							Retry loading configuration
						</Button>
					)}
				</>
			) : (
				<>
					<div
						aria-label="Draft staging"
						className="space-y-2 rounded-lg border border-secondary p-4"
					>
						<h3 className="font-semibold">Draft</h3>
						<p>
							Draft version {snapshot.version} · {snapshot.draft.sources.length}{" "}
							sources · {snapshot.draft.integrations.length} integrations
							{dirty ? " · Active form has unsaved changes" : " · Saved"}
						</p>
						{submitted &&
							Object.keys(errors).map((id) => (
								<p key={id}>
									<button
										className="text-error-primary underline"
										data-form-id={id}
										onClick={showFormErrors}
									>
										{id === active?.item.id
											? active.item.fields.name || "Current form"
											: (snapshot.draft.sources.find((item) => item.id === id)
													?.fields.name ??
												snapshot.draft.integrations.find(
													(item) => item.id === id,
												)?.fields.name ??
												"Draft")}
										: check required values
									</button>
								</p>
							))}
						{undo && (
							<p>
								{undo.name} was removed.{" "}
								<Button
									color="secondary"
									isDisabled={locked}
									onClick={undoRemoval}
									size="sm"
								>
									Undo removal
								</Button>
							</p>
						)}
						<Button
							color="secondary"
							isDisabled={locked}
							onClick={() => void reload()}
							size="sm"
						>
							Load latest saved draft
						</Button>
					</div>
					<div
						aria-label="Configuration workspace"
						className="flex flex-wrap gap-2 border-b border-secondary pb-3"
						onKeyDown={(event) => {
							if (
								!["ArrowLeft", "ArrowRight", "End", "Home"].includes(event.key)
							)
								return;
							event.preventDefault();
							const index = tabs.indexOf(tab);
							const next =
								event.key === "Home"
									? tabs[0]
									: event.key === "End"
										? tabs[2]
										: tabs[(index + (event.key === "ArrowRight" ? 1 : 2)) % 3];
							if (next) navigateTo(next);
						}}
						role="tablist"
					>
						{tabs.map((name) => (
							<button
								aria-controls={`panel-${name}`}
								aria-selected={tab === name}
								className={`rounded-md px-3 py-2 text-sm ${tab === name ? "bg-brand-600 text-white" : "border border-secondary"} ${notice.includes("saved to draft") && name === "Review and publish" ? "ring-2 ring-brand-600" : ""}`}
								disabled={locked}
								id={`tab-${name}`}
								key={name}
								onClick={() => navigateTo(name)}
								role="tab"
								tabIndex={tab === name ? 0 : -1}
							>
								{name}
							</button>
						))}
					</div>
					<div
						aria-labelledby={`tab-${tab}`}
						className="min-w-0 space-y-4"
						id={`panel-${tab}`}
						role="tabpanel"
					>
						{tab === "Sources" && (
							<div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(12rem,16rem)_minmax(0,1fr)]">
								<nav
									aria-label="Draft sources and integrations"
									className="min-w-0 space-y-3"
								>
									<Button
										id="configuration-add-source"
										isDisabled={locked}
										onClick={beginAdd}
										size="sm"
									>
										Add new source
									</Button>
									{!snapshot.draft.sources.length && (
										<p className="text-sm text-secondary">
											No sources in the saved draft.
										</p>
									)}
									{snapshot.draft.sources.map((item) => (
										<button
											aria-pressed={active?.item.id === item.id && !chooser}
											className={`block w-full rounded-md border border-secondary px-3 py-2 text-left break-words ${active?.item.id === item.id && !chooser ? "bg-secondary" : ""}`}
											disabled={locked}
											key={item.id}
											onClick={() =>
												guard(() =>
													select(
														openForm(
															currentSnapshot.current?.draft ?? snapshot.draft,
															"sources",
															item.id,
														),
													),
												)
											}
										>
											{item.fields.name}
										</button>
									))}
									<h3 className="pt-3 font-semibold">Shared integrations</h3>
									{snapshot.draft.integrations.map((item) => (
										<button
											aria-pressed={active?.item.id === item.id && !chooser}
											className="block w-full rounded-md border border-secondary px-3 py-2 text-left break-words"
											disabled={locked}
											key={item.id}
											onClick={() =>
												guard(() =>
													select(
														openForm(
															currentSnapshot.current?.draft ?? snapshot.draft,
															"integrations",
															item.id,
														),
													),
												)
											}
										>
											{item.fields.name}
										</button>
									))}
									<Button
										color="secondary"
										isDisabled={locked}
										onClick={() =>
											guard(() => {
												select({
													item: newIntegration(),
													kind: "integrations",
												});
												focusName();
											})
										}
										size="sm"
									>
										Add integration
									</Button>
									<Button
										color="secondary"
										isDisabled={locked}
										onClick={() =>
											guard(() => {
												setActive(undefined);
												setImporting(true);
												setChooser(false);
												clearValidation();
											})
										}
										size="sm"
									>
										Import CSV
									</Button>
								</nav>
								<div className="min-w-0 space-y-4">
									{chooser ? (
										<section className="space-y-4">
											<h3 className="font-semibold">Choose a source type</h3>
											<div className="grid gap-2 sm:grid-cols-2">
												{edgeSourceTypes.map((value) => (
													<button
														aria-pressed={type === value}
														className={`rounded-md border border-secondary p-3 text-left ${type === value ? "bg-secondary ring-2 ring-brand-600" : ""}`}
														key={value}
														onClick={() => setType(value)}
													>
														<span className="block font-medium">
															{sourceLabels[value]}
														</span>
														<span className="text-sm text-secondary">
															{sourceDescriptions[value]}
														</span>
													</button>
												))}
											</div>
											<Button
												color="secondary"
												onClick={() => setChooser(false)}
												size="sm"
											>
												Cancel
											</Button>{" "}
											<Button onClick={createSource} size="sm">
												Create source form
											</Button>
										</section>
									) : importing ? (
										<section className="space-y-3">
											<h3 className="font-semibold">Import sources from CSV</h3>
											<p>
												Preview this batch before adding it to the saved draft.
												Existing sources are preserved.
											</p>
											<input
												accept=".csv,text/csv"
												aria-label="CSV file"
												disabled={locked}
												onChange={(event) => {
													void readCsv(event.target.files?.[0]);
													event.target.value = "";
												}}
												type="file"
											/>
											{csv && (
												<>
													<ul>
														{csv.draft.sources
															.slice(snapshot.draft.sources.length)
															.map((item) => (
																<li key={item.id}>
																	{item.fields.name} ·{" "}
																	{sourceLabels[item.fields.source_type]}
																</li>
															))}
													</ul>
													<Button
														isDisabled={
															locked || csv.version !== snapshot.version
														}
														onClick={() => {
															if (
																csv.version === snapshot.version &&
																!Object.keys(
																	draftErrors(
																		csv.draft,
																		snapshot.unreadableSourceUrls,
																	),
																).length
															)
																void execute({
																	configuration: csv.draft,
																	id: crypto.randomUUID(),
																	kind: "save",
																	message:
																		"CSV sources added to the draft. Review and publish to apply changes.",
																	version: snapshot.version,
																});
														}}
														size="sm"
													>
														Add batch to draft
													</Button>
												</>
											)}
										</section>
									) : active ? (
										<div
											key={`${active.item.id}/${formGeneration}`}
											onBlur={(event) => blur(event.target)}
											onChangeCapture={(event) => {
												if (event.target instanceof HTMLElement) {
													const field =
														event.target.closest<HTMLElement>("[data-field]")
															?.dataset.field;
													if (field) editedFields.current.add(field);
												}
											}}
											ref={formNode}
										>
											<FormValidation
												value={{
													errors: shownErrors,
													id: active.item.id,
													integration: active.kind === "integrations",
												}}
											>
												<fieldset
													className="min-w-0 space-y-4"
													disabled={locked}
												>
													{active.kind === "sources" ? (
														<SourceEditor
															installation={installationExternalId}
															integrations={snapshot.draft.integrations}
															item={active.item}
															sourceUrl={snapshot.sourceUrls[active.item.id]}
															sourceUrlUnreadable={snapshot.unreadableSourceUrls.includes(
																active.item.id,
															)}
															update={(item) =>
																updateActive({ ...active, item })
															}
														/>
													) : (
														<IntegrationEditor
															item={active.item}
															update={(item) =>
																updateActive({ ...active, item })
															}
														/>
													)}
													{shownErrors.form && (
														<p className="text-error-primary" role="alert">
															{shownErrors.form}
														</p>
													)}
													<p className="text-sm text-secondary">
														{active.baseline
															? `Saves this ${active.kind === "sources" ? "source's" : "integration's"} changes to the draft.`
															: `Adds this ${active.kind === "sources" ? "source" : "integration"} to the draft.`}
													</p>
													<div
														className="flex flex-wrap gap-2"
														onPointerDown={(event) => {
															if (
																event.target instanceof HTMLElement &&
																event.target.closest("button") &&
																event.pointerType === "mouse"
															)
																event.preventDefault();
														}}
													>
														{active.baseline && (
															<Button
																color="secondary"
																onClick={() =>
																	remove(active.kind, active.item.id)
																}
																size="sm"
															>
																Remove{" "}
																{active.kind === "sources"
																	? "source"
																	: "integration"}{" "}
																from draft
															</Button>
														)}
														<Button
															isDisabled={!dirty || locked}
															onClick={() => void save()}
															size="sm"
														>
															Save to draft
														</Button>
														{dirty && (
															<Button
																color="secondary"
																onClick={abandon}
																size="sm"
															>
																Abandon changes
															</Button>
														)}
													</div>
												</fieldset>
											</FormValidation>
										</div>
									) : (
										<p>
											Select a staged source or integration, or add a new
											source.
										</p>
									)}
									{error && (
										<p className="text-error-primary" role="alert">
											{error}
										</p>
									)}
									{notice && <p role="status">{notice}</p>}
								</div>
							</div>
						)}
						{tab === "Review and publish" && (
							<section className="space-y-4">
								<h3 className="font-semibold">
									Review saved draft version {snapshot.version}
								</h3>
								<p>
									{snapshot.draft.sources.length} sources and{" "}
									{snapshot.draft.integrations.length} shared integrations.
									Unsaved form values are never published.
								</p>
								<ul>
									{snapshot.draft.sources.map((item) => (
										<li key={item.id}>
											{item.fields.name} ·{" "}
											{sourceLabels[item.fields.source_type]} ·{" "}
											{item.desiredState}
										</li>
									))}
								</ul>
								<ul>
									{snapshot.draft.integrations.map((item) => (
										<li key={item.id}>Integration: {item.fields.name}</li>
									))}
								</ul>
								{!!removedSources.length && (
									<p>
										Sources to remove:{" "}
										{removedSources.map((item) => item.name).join(", ")}
									</p>
								)}
								{!!removedIntegrations.length && (
									<p>
										Integrations to remove:{" "}
										{removedIntegrations.map((item) => item.name).join(", ")}
									</p>
								)}
								{!snapshot.draft.sources.length && (
									<p>
										This is an empty source configuration. Any previously
										managed sources will be removed when this revision applies.
									</p>
								)}
								{requiresConfirmation && (
									<label className="flex items-start gap-2">
										<input
											checked={confirmation}
											disabled={locked}
											id="configuration-confirmation"
											onChange={(event) => {
												setConfirmation(event.target.checked);
												setError("");
											}}
											type="checkbox"
										/>
										{confirmationLabel}
									</label>
								)}
								<Button
									isDisabled={
										locked || dirty || publicationConfirmed || reviewStale
									}
									onClick={publish}
									size="sm"
								>
									Publish saved draft
								</Button>
								{error && (
									<p className="text-error-primary" role="alert">
										{error}
									</p>
								)}
								{notice && <p role="status">{notice}</p>}
								<ConfigurationStatus
									desired={reportedDesired}
									now={now}
									refreshFailed={refreshFailed}
									reports={snapshot.reports}
								/>
							</section>
						)}
						{tab === "Publication history" && (
							<section className="space-y-4">
								<h3 className="font-semibold">Publication history</h3>
								{!history.length && (
									<p>No publication history is currently loaded.</p>
								)}
								{history.map((item) => (
									<div
										className="space-y-2 rounded-md border border-secondary p-3"
										key={item.revision}
									>
										<p>
											{timestamp(item.publishedAt)} · {item.publishedBy}
										</p>
										<p className="font-mono text-xs break-all">
											{item.revision}
										</p>
										<Button
											color="secondary"
											isDisabled={locked}
											onClick={() => void inspect(item.revision)}
											size="sm"
										>
											Inspect revision
										</Button>
									</div>
								))}
								{inspected && (
									<section className="space-y-3 rounded-md border border-secondary p-4">
										<h4 className="font-semibold">Revision content</h4>
										<p className="font-mono text-xs break-all">
											{inspected.revision}
										</p>
										<RevisionContents
											draft={inspected.draft}
											sourceUrls={inspected.sourceUrls}
											unreadableSourceUrls={inspected.unreadableSourceUrls}
										/>
										<Button
											isDisabled={locked}
											onClick={() => copyRevision(inspected.revision)}
											size="sm"
										>
											Copy revision to draft
										</Button>
									</section>
								)}
								{error && (
									<p className="text-error-primary" role="alert">
										{error}
									</p>
								)}
								{notice && <p role="status">{notice}</p>}
							</section>
						)}
					</div>
					{pending && (
						<div
							aria-label="Operation recovery"
							className="space-y-2 rounded-lg border border-secondary p-4"
							role="region"
						>
							<p>
								{busy
									? "Waiting for the operation…"
									: "Resolve this operation before making another change."}
							</p>
							<Button
								color="secondary"
								isDisabled={busy}
								onClick={() => void execute(pending, true)}
								size="sm"
							>
								Check again
							</Button>{" "}
							<Button
								isDisabled={busy}
								onClick={() => void execute(pending)}
								size="sm"
							>
								Retry{" "}
								{pending.kind === "publish" ? "publication" : "same operation"}
							</Button>
						</div>
					)}
				</>
			)}
			<dialog
				aria-labelledby="leave-form-title"
				className="max-w-lg space-y-4 rounded-lg border border-secondary bg-primary p-6 text-primary backdrop:bg-black/50"
				onCancel={(event) => {
					event.preventDefault();
					cancelNavigation();
				}}
				ref={dialog}
			>
				<h3 className="font-semibold" id="leave-form-title">
					Save or abandon this form?
				</h3>
				<p>
					Save this form to the draft or abandon its changes before continuing.
					Other forms will not retain unsaved values.
				</p>
				{submitted && Object.keys(errors).length > 0 && (
					<p role="alert">
						This form needs attention. Cancel to correct the highlighted fields.
					</p>
				)}
				{error && <p role="alert">{error}</p>}
				<div className="flex flex-wrap gap-2">
					<Button
						isDisabled={locked}
						onClick={async () => {
							const next = navigation;
							if (await save()) {
								setNavigation(undefined);
								next?.proceed();
							} else {
								next?.cancel?.();
								setNavigation(undefined);
								revealErrors();
							}
						}}
						size="sm"
					>
						Save to draft and continue
					</Button>
					<Button
						color="secondary"
						isDisabled={locked}
						onClick={() => {
							const next = navigation;
							abandon();
							setNavigation(undefined);
							next?.proceed();
						}}
						size="sm"
					>
						Abandon changes and continue
					</Button>
					<Button
						color="secondary"
						isDisabled={busy}
						onClick={cancelNavigation}
						size="sm"
					>
						Cancel
					</Button>
				</div>
			</dialog>
		</section>
	);
}
